import axios from 'axios';

// Get API base URL from environment variable or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8008';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    // Required so the httponly `refresh_token` cookie set at login is sent back
    // on /auth/refresh (and any other credentialed request).
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auth APIs
export const login = (data) => api.post('/auth/login', data);
export const logout = () => api.post('/auth/logout');
export const getProfile = () => api.get('/auth/me');
export const getMyCompanies = () => api.get('/auth/companies');
export const switchMyCompany = (companyId) => api.post('/auth/companies/switch', { company_id: companyId });

// Silent token refresh.
//
// The access token (local JWT) expires after JWT_EXPIRACY_HOURS. Before this
// change, the next request after expiry returned 401 and we logged the user out
// — even though they were actively using the app. The backend issues a 7-day
// httponly `refresh_token` cookie at login, so instead we transparently mint a
// fresh access token via /auth/refresh and replay the failed request. The user
// is only logged out when the refresh itself fails (cookie gone/expired/revoked
// or the external auth service rejects them).

const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
};

const forceLogout = () => {
    clearSession();
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

// Single-flight: many requests may 401 at once; only one /auth/refresh runs and
// the rest await its result.
let refreshPromise = null;

const refreshAccessToken = () => {
    if (!refreshPromise) {
        // Bare axios (not `api`) so this request can't recurse through the
        // response interceptor. Still send the httponly refresh cookie.
        refreshPromise = axios
            .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
            .then((res) => {
                const newToken = res.data?.access_token;
                if (!newToken) throw new Error('No access_token in refresh response');
                localStorage.setItem('token', newToken);
                return newToken;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

// Response interceptor: on 401, attempt a one-time silent refresh + retry.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config || {};
        const requestUrl = originalRequest.url || '';
        const isLoginRequest = requestUrl.includes('/auth/login');
        const isRefreshRequest = requestUrl.includes('/auth/refresh');

        // 403 = authenticated but unauthorized — surface to caller, never logout.
        // 401 on /auth/login (bad credentials) — let the Login page show it.
        // 401 on /auth/refresh — refresh itself failed, session is truly dead.
        if (error.response?.status !== 401 || isLoginRequest || isRefreshRequest) {
            if (isRefreshRequest) forceLogout();
            return Promise.reject(error);
        }

        // Only retry once per request to avoid loops.
        if (originalRequest._retry) {
            forceLogout();
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        try {
            const newToken = await refreshAccessToken();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
        }
    }
);

// Database Management APIs
export const testConnection = (data) => api.post('/api/database/test-connection', data);
export const connectDatabase = (data) => api.post('/api/database/connect', data);
export const getDatabases = () => api.get('/api/database');
export const deleteDatabase = (id) => api.delete(`/api/database/${id}`);
export const updateDatabase = (id, data) => api.patch(`/api/database/${id}`, data);
export const getChartLibrarySettings = (databaseId) =>
    api.get(`/api/database/${databaseId}/chart-library-settings`);
export const putChartLibrarySettings = (databaseId, payload) =>
    api.put(`/api/database/${databaseId}/chart-library-settings`, payload);

// Schema Analysis APIs
export const analyzeSchema = (id, options = {}) =>
    api.post(`/api/schema/analyze/${id}`, options);
export const getTablesInfo = (id) => api.get(`/api/schema/tables/${id}`);
export const getTableSelection = (id) => api.get(`/api/schema/table-selection/${id}`);
// payload: { tables: [{ table_name, is_active }, ...] }
export const updateTableSelection = (id, payload) =>
    api.put(`/api/schema/table-selection/${id}`, payload);
export const getSchemaAnalysis = (id) => api.get(`/api/schema/${id}`);
export const getSemanticAnalysis = (id) => api.get(`/api/semantic/${id}`);
export const getDataProfiling = (id) => api.get(`/api/dataprofiling/${id}`);

// Query Engine APIs - Database-Based Flow
export const generateDatabaseInsights = (databaseId) => api.post('/api/database/insights/generate', { database_id: databaseId });
export const getDatabaseInsights = (databaseId, source = 'database_analysis', limit = 100) =>
    api.get(`/api/database/insights?database_id=${databaseId}&source=${source}&limit=${limit}`);
export const generateDatabaseQueries = (databaseId) => api.post('/api/database/queries/generate', { database_id: databaseId });
export const getDatabaseQueries = (databaseId, validationStatus = null, limit = 100) => {
    let url = `/api/database/queries?database_id=${databaseId}&limit=${limit}`;
    if (validationStatus) url += `&validation_status=${validationStatus}`;
    return api.get(url);
};

// Query Engine APIs - NLP-Based Flow
export const generateNLPInsights = (data) => api.post('/api/nlp/insights/generate', data);
export const generateNLPQuery = (data) => api.post('/api/nlp/queries/generate', data);
export const getNLPQueries = (databaseId, limit = 100) => api.get(`/api/nlp/queries?database_id=${databaseId}&limit=${limit}`);

// Query Engine APIs - Combined
export const getAllQueries = (databaseId, source = null, validationStatus = null, limit = 200) => {
    let url = `/api/queries/all?database_id=${databaseId}&limit=${limit}`;
    if (source) url += `&source=${source}`;
    if (validationStatus) url += `&validation_status=${validationStatus}`;
    return api.get(url);
};

// Query execution (used by the NLP/chat-refine flow below)
export const executeQuery = (data) => api.post('/api/query/execute', data);

// NLP Query Chat Refinement APIs
export const refineNLPQuery = (data) => api.post('/api/query/chat/refine', data);
export const updateNLPQuery = (queryId, data) => api.patch(`/api/query/${queryId}`, data);
export const testRefinedQuery = (data) => api.post('/api/query/test-refined', data);

// Query Execution History API
export const getQueryExecutions = (queryId, limit = 50) => api.get(`/api/query/${queryId}/executions?limit=${limit}`);

// Simple Query Execution (no AI analysis) - for compare view
export const executeQuerySimple = (data) => api.post('/api/query/execute', data);

// Chart Generation APIs (new module-based approach)
// Generate charts for all database queries (non-NLP case)
export const generateChartsForDatabase = (databaseId) => api.post(`/api/chart/generate/${databaseId}`);

// Generate charts for a specific NLP query
export const generateChartsForNLPQuery = (databaseId, queryId) => api.post(`/api/chart/generate-nlp/${databaseId}/${queryId}`);

// Get chart visualization data for a query
export const getChartVisualization = (databaseId, queryId) => api.get(`/api/chart/visualization/${databaseId}/${queryId}`);

// Get chart generation status for DATABASE-BASED queries
export const getDatabaseQueriesChartStatus = (databaseId) => api.get(`/api/chart/generation-status/database/${databaseId}`);

// Get chart generation status for NLP-GENERATED queries
export const getNLPQueriesChartStatus = (databaseId) => api.get(`/api/chart/generation-status/nlp/${databaseId}`);

// Legacy chart API (for backward compatibility)
export const generateChartRecommendations = (queryId, databaseId) => api.post(`/api/chart/generate/${queryId}?database_id=${databaseId}`);
export const getChartRecommendations = (queryId) => api.get(`/api/chart/recommendations/${queryId}`);
export const generateAllCharts = (databaseId) => api.post(`/api/chart/generate-all/${databaseId}`);
export const getChartStatusForDatabase = (databaseId) => api.get(`/api/chart/status/${databaseId}`);
export const getChartStatusForQuery = (databaseId, queryId) => api.get(`/api/chart/status/${databaseId}/${queryId}`);

// Endpoint Generation APIs
export const generateEndpoints = (id) => api.post(`/api/endpoint/generate/${id}`);
export const getGeneratedEndpoints = (id) => api.get(`/api/generate-endpoint/database/${id}`);
export const downloadEndpointsDoc = (id) => {
    return api.get(`/api/endpoints/download-document/${id}`, {
        responseType: 'blob',
    });
};

// Dynamic Endpoints Management APIs
export const enableDynamicEndpoint = (id) => api.post(`/api/dynamic-endpoint/enable/${id}`);
export const getDatabaseStatus = () => api.get('/api/dynamic-endpoint/database-status');
export const disableDynamicEndpoint = (id) => api.post(`/api/dynamic-endpoint/disable/${id}`);

// System & Progress APIs
export const getDatabaseProgress = (databaseId) => api.get(`/api/system/progress/${databaseId}`);
export const getSystemStats = () => api.get('/api/system/stats');
export const getHealth = () => api.get('/api/health');
export const getRecentActivity = () => api.get('/api/system/recent-activity');

// Alias for backward compatibility
export const healthCheck = getHealth;

// Report Generation APIs
export const createReportGeneration = (data) => api.post('/api/reports/generate', data);
export const executeReportGeneration = (data) => api.post('/api/reports/generate/execute', data);
export const syncReport = (data) => api.post('/api/reports/sync', data);
export const getReportGeneration = (id) => api.get(`/api/reports/${id}`);
export const updateReportPlan = (id, data) =>
    api.post(`/api/reports/${id}/update-plan`, data);
export const updateReportTimeFramePlans = (id, data) =>
    api.patch(`/api/reports/${id}/time-frame-plans`, data);
export const updateReportGeneration = (id, data) => api.patch(`/api/reports/${id}`, data);
export const getGeneratedReportUIManifest = (databaseId) =>
    api.get('/api/reports/generated-ui/manifest', { params: { database_id: databaseId } });
export const getReportsForDatabase = (databaseId) => api.get(`/api/reports/database/${databaseId}`);
export const getTimeFrameReport = (reportId, timeFrameType) => api.get(`/api/reports/${reportId}/time-frame/${timeFrameType}`);
export const getTimeFrameVersions = (reportId, timeFrameType) => api.get(`/api/reports/${reportId}/time-frame/${timeFrameType}/versions`);
export const getTimeFrameByIndex = (reportId, timeFrameType, index) => api.get(`/api/reports/${reportId}/time-frame/${timeFrameType}/index/${index}`);
export const updateExecutionPlan = (data) => api.patch('/api/reports/execution-plan', data);
export const deleteReportGeneration = (id) => api.delete(`/api/reports/${id}`);
export const deleteTimeFrameReport = (reportGenerationId, timeFrameId) =>
    api.delete(`/api/reports/${reportGenerationId}/time-frames/${timeFrameId}`);
export const askReportQuestion = (reportId, body) =>
    api.post(`/api/reports/${reportId}/chat`, body);
export const askDatabaseReportQuestion = (databaseId, body) =>
    api.post(`/api/reports/database/${databaseId}/chat`, body);

// Implementation Plans APIs
export const listPlans = (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.databaseId) queryParams.append('database_id', params.databaseId);
    if (params.timeFrameType) queryParams.append('time_frame_type', params.timeFrameType);
    if (params.status) queryParams.append('status', params.status);
    if (params.reportGenerationId) queryParams.append('report_generation_id', params.reportGenerationId);
    if (params.timeFrameReportId) queryParams.append('time_frame_report_id', params.timeFrameReportId);
    const queryString = queryParams.toString();
    return api.get(`/api/reports/plans${queryString ? '?' + queryString : ''}`);
};
export const generatePlans = (data) => api.post('/api/reports/plans/generate', data);
export const getPlan = (planId) => api.get(`/api/reports/plans/${planId}`);
export const updatePlan = (planId, data) => api.patch(`/api/reports/plans/${planId}`, data);
export const evaluatePlan = (planId) => api.post(`/api/reports/plans/${planId}/evaluate`);
export const deletePlan = (planId) => api.delete(`/api/reports/plans/${planId}`);

// Design-mode chat APIs (one-shot commands that edit a generated dashboard .tsx)
export const submitDesignCommand = ({ databaseId, dashboardKind, topicKey, commandText }) =>
    api.post('/api/agents/design-chat/commands', {
        database_id: databaseId,
        dashboard_kind: dashboardKind,
        topic_key: topicKey ?? null,
        command_text: commandText,
    });
export const listDesignCommands = ({ databaseId, dashboardKind, topicKey, commandType }) => {
    const params = { database_id: databaseId, dashboard_kind: dashboardKind };
    if (dashboardKind === 'topic' && topicKey != null) params.topic_key = topicKey;
    if (commandType) params.command_type = commandType;
    return api.get('/api/agents/design-chat/commands', { params });
};
export const getDesignCommand = (commandId) =>
    api.get(`/api/agents/design-chat/commands/${commandId}`);

// Database Understanding APIs
export const createDatabaseUnderstanding = (databaseId, data) => api.post(`/api/database/${databaseId}/database-understandings`, data);
export const getDatabaseUnderstandings = (databaseId) => api.get(`/api/database/${databaseId}/database-understandings`);
export const updateDatabaseUnderstanding = (databaseId, understandingId, data) => api.put(`/api/database/${databaseId}/database-understandings/${understandingId}`, data);
export const deleteDatabaseUnderstanding = (databaseId, understandingId) => api.delete(`/api/database/${databaseId}/database-understandings/${understandingId}`);
export const regenerateDatabaseUnderstanding = (databaseId) => api.post(`/api/database/${databaseId}/database-understandings/regenerate`);

// Admin / Company Management APIs
export const adminListCompanies = () => api.get('/api/admin/companies');
export const adminCreateCompany = (data) => api.post('/api/admin/companies', data);
export const adminUpdateCompany = (companyId, data) => api.put(`/api/admin/companies/${companyId}`, data);
export const adminDeleteCompany = (companyId) => api.delete(`/api/admin/companies/${companyId}`);
export const adminUploadCompanyLogo = (companyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/companies/${companyId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const adminDeleteCompanyLogo = (companyId) => api.delete(`/api/admin/companies/${companyId}/logo`);

export const adminListUsers = () => api.get('/api/admin/users');
export const adminUpdateUser = (username, data) => api.put(`/api/admin/users/${encodeURIComponent(username)}`, data);
export const adminDeleteUser = (username) => api.delete(`/api/admin/users/${encodeURIComponent(username)}`);

export const adminListDatabases = () => api.get('/api/admin/databases');
export const adminAssignDatabaseCompany = (databaseId, data) => api.put(`/api/admin/databases/${databaseId}/company`, data);

export default api;
